import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Lock, ShieldAlert, Mail, RefreshCcw, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";

const CONTACT_EMAIL = "investors@streamline360.ai";

export default function InvitationLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("opening"); // opening | slow | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;
    // Show a "taking longer than usual" hint if network is slow
    const slowTimer = setTimeout(() => {
      if (!cancelled) setStatus((s) => (s === "opening" ? "slow" : s));
    }, 6000);

    (async () => {
      try {
        const { data } = await api.post("/investor/magic", { token });
        if (cancelled) return;
        try { localStorage.setItem("s360_investor", JSON.stringify(data)); } catch {}
        navigate("/", { replace: true });
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setMessage(typeof detail === "string" ? detail : "This invitation has expired or is no longer valid.");
        setStatus("error");
      }
    })();

    return () => { cancelled = true; clearTimeout(slowTimer); };
  }, [token, navigate]);

  if (status === "opening" || status === "slow") {
    return (
      <div
        data-testid="invitation-opening"
        role="status"
        aria-live="polite"
        className="min-h-screen flex flex-col items-center justify-center gap-3 text-ftext3 font-mono-data text-sm bg-ink px-6 text-center"
      >
        <span>Opening your Streamline Investor Pitch Deck…</span>
        {status === "slow" && (
          <div data-testid="invitation-slow-hint" className="text-xs text-ftext3/80 max-w-sm leading-relaxed">
            Taking longer than usual. If this persists, please refresh the page or contact the Streamline360 team.
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="invitation-error" className="min-h-screen flex items-center justify-center bg-ink px-6">
      <main role="main" aria-labelledby="invite-error-title" className="max-w-lg w-full border border-line bg-panel p-8">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 font-mono-data text-xs uppercase tracking-wider text-ftext2">
            <Lock className="h-4 w-4" /> Invitation only
          </div>
          <Logo className="h-6 w-auto" />
        </div>
        <div className="flex items-start gap-3 mb-4">
          <ShieldAlert className="w-6 h-6 text-sky mt-0.5 shrink-0" />
          <div>
            <h1 id="invite-error-title" className="font-heading font-black text-xl text-ftext leading-tight mb-1">
              This invitation has expired or is no longer valid
            </h1>
            <p className="text-sm text-ftext2 leading-relaxed">
              {message || "Reply to your invitation email or contact the Streamline360 team to request a new link."}
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-2">
          <a
            data-testid="invitation-request-new"
            href={`mailto:${CONTACT_EMAIL}?subject=Streamline360%20Investor%20Deck%20-%20New%20invitation%20request&body=Hello%20Streamline360%20team,%0A%0AMy%20invitation%20link%20is%20no%20longer%20valid.%20Could%20you%20please%20send%20me%20a%20fresh%20personal%20link%20to%20the%20investor%20deck?%0A%0AThank%20you.`}
            className="w-full flex items-center justify-between border border-sky bg-sky/10 text-sky hover:bg-sky/20 px-4 py-3 font-mono-data text-xs font-bold uppercase tracking-wider transition-colors"
          >
            <span className="inline-flex items-center gap-2"><RefreshCcw className="w-3.5 h-3.5" /> Request a new invitation</span>
            <span aria-hidden>→</span>
          </a>
          <a
            data-testid="invitation-contact-team"
            href={`mailto:${CONTACT_EMAIL}?subject=Streamline360%20-%20Investor%20deck%20access`}
            className="w-full flex items-center justify-between border border-line2 bg-panel hover:border-sky hover:text-sky text-ftext2 px-4 py-3 font-mono-data text-xs font-bold uppercase tracking-wider transition-colors"
          >
            <span className="inline-flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Contact the team</span>
            <span className="normal-case tracking-normal text-ftext3">{CONTACT_EMAIL}</span>
          </a>
          <Link
            data-testid="invitation-return-portal"
            to="/"
            className="w-full flex items-center justify-between border border-line2 bg-panel hover:border-sky hover:text-sky text-ftext2 px-4 py-3 font-mono-data text-xs font-bold uppercase tracking-wider transition-colors"
          >
            <span className="inline-flex items-center gap-2"><ArrowLeft className="w-3.5 h-3.5" /> Return to portal</span>
            <span aria-hidden>→</span>
          </Link>
        </div>

        <div className="mt-6 flex items-center justify-between pt-4 border-t border-line">
          <span className="font-mono-data text-xs uppercase tracking-wider text-ftext3">Confidential — do not forward</span>
          <Link
            data-testid="invitation-founder-access"
            to="/login"
            className="font-mono-data text-xs uppercase tracking-wider text-ftext3 hover:text-sky transition-colors"
          >
            Founder access →
          </Link>
        </div>
      </main>
    </div>
  );
}
